import { ChevronDown, LogOut, Settings, UserCog } from 'lucide-react';
import { api } from '../lib/api';
import { useRevalidarSessao, useSessao } from '../lib/session';
import { useToast } from '../ui';
import { usePainel } from './usePainel';
import './header.css';

const PAPEL: Record<string, string> = {
  ADMIN: 'Administrador',
  SUPERVISOR: 'Supervisor',
  AGENT: 'Atendente',
};

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter((p) => !/^(de|da|do|das|dos|e)$/i.test(p));
  return ((partes[0]?.[0] ?? '?') + (partes.length > 1 ? partes.at(-1)![0] : '')).toUpperCase();
}

/**
 * Identidade do usuário na barra superior.
 *
 * Antes disto não havia nenhuma forma de sair da conta pela interface — o
 * caminho era apagar o cookie. Num produto multi-tenant onde a mesma máquina é
 * usada por vários atendentes, isso não é uma falta de conveniência.
 */
export function HeaderUsuario({ onAbrirConfiguracoes }: { onAbrirConfiguracoes: () => void }) {
  const { agent, tenant } = useSessao();
  const revalidar = useRevalidarSessao();
  const toast = useToast();
  const { aberto, setAberto, raiz, gatilho } = usePainel();

  async function sair() {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      // Falha de rede no logout não pode prender ninguém na sessão: seguimos
      // para a revalidação, que devolve 401 e leva à tela de entrada.
    }
    revalidar();
    toast('Sessão encerrada.');
  }

  return (
    <div className="painel-ancora" ref={raiz}>
      <button
        ref={gatilho}
        type="button"
        className="header-botao"
        aria-expanded={aberto}
        aria-haspopup="menu"
        onClick={() => setAberto(!aberto)}
      >
        <span className="header-avatar" aria-hidden="true">{iniciais(agent.name)}</span>
        <span className="header-nome">
          <b>{agent.name}</b>
          <span>{PAPEL[agent.role] ?? agent.role}</span>
        </span>
        <ChevronDown size={16} aria-hidden="true" />
        <span className="sr-only">Abrir menu da conta</span>
      </button>

      {aberto && (
        <div className="painel-flutuante" role="menu" aria-label="Conta">
          {/* O e-mail e o workspace ficam aqui, não no botão: numa máquina
              compartilhada, saber em qual conta e em qual workspace você está
              antes de agir evita mandar mensagem pelo número errado. */}
          <div className="menu-cabeca">
            <b>{agent.name}</b>
            <span>{agent.email}</span>
            <span>{`Workspace: ${tenant.name}`}</span>
          </div>
          <div className="menu-sep" />
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => { setAberto(false); onAbrirConfiguracoes(); }}
          >
            <UserCog size={18} aria-hidden="true" />
            Configurações do perfil
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => { setAberto(false); onAbrirConfiguracoes(); }}
          >
            <Settings size={18} aria-hidden="true" />
            Configurações do workspace
          </button>
          <div className="menu-sep" />
          <button
            type="button"
            role="menuitem"
            className="menu-item menu-item--perigo"
            onClick={() => { setAberto(false); void sair(); }}
          >
            <LogOut size={18} aria-hidden="true" />
            Sair da conta
          </button>
        </div>
      )}
    </div>
  );
}
